import { RootState } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { ICalcs, IQuestionById } from './constants/queryCreation.interface';
import { IOutputItem } from 'Modules/Studio/Questions/EditQuestion/SubQuestions/redux/constants/subQuestions.interface';
import {
  getExistingProps,
  getPropConfig,
} from '@components/Studio/QueryCreationTwo/Props/propUtils';
import { getPathVariables } from './utils/getPathVariables';
import { getPropMapByConcept } from '@components/Studio/QueryCreationTwo/ActionButtons/Visualization/helpers';
import { IQueryCreationState } from './constants/queryCreation.initialState.interface';
import { getPropType } from 'utils/Studio/properties';
import { timeseriesPropType } from 'utils/Studio/SelectOptions';
import {
  convertFormulaString,
  getAllConceptLabelsAndOutputKeyNames,
  TAllConceptLabelsAndOutputKeyNames,
} from './utils';

const selectedConceptId = (state: RootState) =>
  (state.queryCreation as IQueryCreationState).selectedConceptId;
export const query = (state: RootState) => (state.queryCreation as IQueryCreationState).query;
export const queryMap = (state: RootState) => (state.queryCreation as IQueryCreationState).queryMap;
export const calcsList = (state: RootState) =>
  (state.queryCreation as IQueryCreationState).calcsList;

export const selectHasTimeSeriesProp = createSelector([queryMap], (queryMap) => {
  return Object.keys(queryMap).some((concept) => {
    return queryMap[concept].properties.some(
      (prop) => getPropType(prop.propTypeUri) === timeseriesPropType[0]
    );
  });
});

export const getIsConceptSelected = createSelector(
  [selectedConceptId],
  (selectedConceptId) => selectedConceptId !== null
);

export const selectHasCalc = createSelector(
  [
    calcsList,
    (state: RootState, propUri: string) => propUri,
    (state: RootState, propUri: string, conceptId: string) => conceptId,
  ],
  (calcsList, propUri, conceptId) => {
    return calcsList.some((calc: ICalcs) =>
      calc.arguments.some((arg) => propUri === arg.property && conceptId === arg.concept)
    );
  }
);

export const selectCardOutput = createSelector(
  [
    query,
    (query: RootState, state: RootState) => (state.queryCreation as IQueryCreationState).calcsList,
    (query: RootState, calcsList: RootState, prop: IOutputItem) => prop,
    (query: RootState, calcsList: RootState, prop: IOutputItem, hasCalc: boolean) => hasCalc,
  ],
  (query, calcsList, prop, hasCalc) => {
    return getPropConfig(prop, query.runtimeParams && query.runtimeParams.sort, hasCalc, calcsList);
  }
);

export const selectPathVariables = createSelector([query], (query) =>
  getPathVariables(query?.definition || {}, query?.filters || [])
);

export const selectExistingQueryProperties = createSelector([queryMap], (queryMap) =>
  getExistingProps(queryMap)
);

export const selectPropMapByConcept = createSelector(
  [selectExistingQueryProperties],
  (existingQueryProperties) => getPropMapByConcept(existingQueryProperties)
);

type TCalcsUsedByProp = Pick<ICalcs, 'outputKeyName' | 'id' | 'description'>;

export const selectCalcsUsedByProperty = createSelector(
  [calcsList, (_calcsList, uri) => uri, (_calcList, _uri, conceptId) => conceptId],
  (calcsList, uri, conceptId): TCalcsUsedByProp[] => {
    return calcsList.reduce((acc: TCalcsUsedByProp[], calc: ICalcs) => {
      const propUsedInCalc = calc.arguments.some(
        (arg) => uri === arg.property && conceptId === arg.concept
      );
      if (propUsedInCalc) {
        return [
          ...acc,
          { outputKeyName: calc.outputKeyName, id: calc.id, description: calc.description },
        ];
      }

      return acc;
    }, []);
  }
);

export const selectQueryConceptLabelsAndOutputCategories = createSelector(
  [
    query,
    (_query, selectedLabel) => selectedLabel,
    (_query, _selectedLabel, selectedOutputCategory) => selectedOutputCategory,
  ],
  (query, selectedLabel, selectedOutputCategory): TAllConceptLabelsAndOutputKeyNames => {
    return getAllConceptLabelsAndOutputKeyNames(
      query as IQuestionById,
      selectedLabel,
      selectedOutputCategory
    );
  }
);

/**
 * Used in calc cards, to display the formula preview w/the subQuery name, concept & output names
 */
export const selectSubQueryName = createSelector([query, (_query, calc) => calc], (query, calc) => {
  const updatedFormulaStr = convertFormulaString(calc.expression, query as IQuestionById);
  return updatedFormulaStr;
});
